/**
 * The shared navigation grammar for every RecallVault client.
 *
 * A destination names one real domain object — a conversation, a request, a run,
 * a result, an alert, a note — rather than a screen. Both the web client and the
 * Apple client parse and format these same canonical strings; they differ only
 * in the prefix they wear (`#/` in a browser URL, `recallvault://` in a deep
 * link). Keeping the grammar in one place is what stops the two clients from
 * drifting into different mental models of the same server.
 *
 * The rule this module exists to guarantee is `alertDestination`: an alert opens
 * the object that explains it, never a generic inbox.
 */

import {
  NAVIGATION_ID_SEGMENTS,
  NAVIGATION_SUBJECT_DESTINATIONS,
  NAVIGATION_SUBJECT_INDEXES,
} from "./generated/navigation-contract.js";

/** Every addressable surface. Adding one means adding a row to both clients. */
export type DestinationKind =
  | "vault"
  | "note"
  | "search"
  | "conversation"
  | "request"
  | "ledger-request"
  | "run"
  | "result"
  | "artifact"
  | "alerts"
  | "alert-history"
  | "alert"
  | "questions"
  | "job"
  | "notice"
  | "sources"
  | "source"
  | "account";

export type VaultFilter = "today" | "all" | "pinned" | "recent" | "queued" | "conflict";
export type SearchMode = "exact" | "semantic";

export type Destination =
  | { readonly kind: "vault"; readonly filter: VaultFilter }
  | { readonly kind: "note"; readonly id: string }
  | { readonly kind: "search"; readonly query: string; readonly mode: SearchMode }
  | { readonly kind: "conversation"; readonly id: string }
  | { readonly kind: "request"; readonly id: string }
  | { readonly kind: "ledger-request"; readonly id: string }
  | { readonly kind: "run"; readonly id: string }
  | { readonly kind: "result"; readonly id: string }
  | { readonly kind: "artifact"; readonly id: string }
  | { readonly kind: "alerts" }
  | { readonly kind: "alert-history" }
  | { readonly kind: "alert"; readonly id: string }
  | { readonly kind: "questions" }
  | { readonly kind: "job"; readonly id: string }
  | { readonly kind: "notice"; readonly id: string }
  | { readonly kind: "sources" }
  | { readonly kind: "source"; readonly id: string }
  | { readonly kind: "account" };

/** Where an unparseable or unknown address lands. */
export const DEFAULT_DESTINATION: Destination = { kind: "vault", filter: "all" };

/**
 * The server's own id rule (`domain-api.ts`). A link carrying an id that fails
 * it does not parse at all, so a malformed address lands on the vault rather
 * than on a half-built page addressed to nothing.
 */
const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]{1,127}$/;

export function isValidId(value: string): boolean {
  return ID_PATTERN.test(value);
}

const VAULT_FILTERS: readonly VaultFilter[] = ["today", "all", "pinned", "recent", "queued", "conflict"];
const SEARCH_MODES: readonly SearchMode[] = ["exact", "semantic"];

/** Single-segment-plus-id destinations, keyed by their path prefix. */
const ID_SEGMENTS: Readonly<Record<string, Extract<Destination, { id: string }>["kind"]>> = NAVIGATION_ID_SEGMENTS;

function withId(kind: Extract<Destination, { id: string }>["kind"], id: string): Destination | undefined {
  return isValidId(id) ? ({ kind, id } as Destination) : undefined;
}

/** The canonical string form, without any client's prefix. */
export function formatDestination(destination: Destination): string {
  switch (destination.kind) {
    case "vault":
      return destination.filter === "all" ? "vault" : `vault?filter=${destination.filter}`;
    case "note":
      return `vault/note/${encodeURIComponent(destination.id)}`;
    case "search": {
      const query = new URLSearchParams({ q: destination.query, mode: destination.mode });
      return `vault/search?${query.toString()}`;
    }
    case "alerts":
      return "alerts";
    case "questions":
      return "questions";
    case "alert-history":
      return "alerts/history";
    case "sources":
      return "sources";
    case "source":
      return `sources/${encodeURIComponent(destination.id)}`;
    case "account":
      return "account";
    default:
      return `${destination.kind}/${encodeURIComponent(destination.id)}`;
  }
}

function parseVault(segments: readonly string[], params: URLSearchParams): Destination | undefined {
  if (segments.length === 1) {
    const filter = params.get("filter") ?? "all";
    return VAULT_FILTERS.includes(filter as VaultFilter)
      ? { kind: "vault", filter: filter as VaultFilter }
      : DEFAULT_DESTINATION;
  }
  if (segments[1] === "note" && segments.length === 3) return withId("note", segments[2] ?? "");
  if (segments[1] === "search" && segments.length === 2) {
    const mode = params.get("mode") ?? "exact";
    return {
      kind: "search",
      query: params.get("q") ?? "",
      mode: SEARCH_MODES.includes(mode as SearchMode) ? (mode as SearchMode) : "exact",
    };
  }
  return undefined;
}

/**
 * Reads a canonical address. Returns `undefined` for anything this client does
 * not understand, so the caller decides between the default destination and
 * telling the user the link was not recognised.
 */
export function parseDestination(raw: string): Destination | undefined {
  const trimmed = raw.trim().replace(/^#/, "").replace(/^recallvault:\/\//, "").replace(/^\/+/, "");
  if (trimmed === "") return DEFAULT_DESTINATION;
  const [path = "", search = ""] = trimmed.split("?", 2);
  const params = new URLSearchParams(search);
  const segments = path.split("/").filter((part) => part !== "").map(decodeURIComponent);
  const [head] = segments;
  if (head === undefined) return DEFAULT_DESTINATION;

  if (head === "vault") return parseVault(segments, params);
  if (head === "alerts") {
    if (segments.length === 1) return { kind: "alerts" };
    return segments.length === 2 && segments[1] === "history" ? { kind: "alert-history" } : undefined;
  }
  if (head === "sources") {
    if (segments.length === 1) return { kind: "sources" };
    return segments.length === 2 ? withId("source", segments[1] ?? "") : undefined;
  }
  if (head === "questions") return segments.length === 1 ? { kind: "questions" } : undefined;
  if (head === "account") return segments.length === 1 ? { kind: "account" } : undefined;

  const kind = ID_SEGMENTS[head];
  if (kind && segments.length === 2) return withId(kind, segments[1] ?? "");
  return undefined;
}

/** The address as this browser writes it into `location.hash`. */
export function webHref(destination: Destination): string {
  return `#/${formatDestination(destination)}`;
}

/** The address as a push payload or a cross-client link carries it to Apple. */
export function appleUrl(destination: Destination): string {
  return `recallvault://${formatDestination(destination)}`;
}

/** A short human name, used for titles, breadcrumbs, and back buttons. */
export function destinationTitle(destination: Destination): string {
  switch (destination.kind) {
    case "vault":
      return ({ today: "Today", all: "All notes", pinned: "Pinned", recent: "Recently changed", queued: "Waiting to sync", conflict: "Needs attention" })[destination.filter];
    case "note":
      return "Note";
    case "search":
      return destination.query ? `Search: ${destination.query}` : "Search";
    case "conversation":
      return "Conversation";
    case "request":
      return "Request";
    case "ledger-request":
      return "Question";
    case "run":
      return "Run";
    case "result":
      return "Result";
    case "artifact":
      return "Artifact";
    case "alerts":
      return "Alerts";
    case "alert-history":
      return "Alert history";
    case "alert":
      return "Alert";
    case "questions":
      return "Questions";
    case "job":
      return "Job";
    case "notice":
      return "Notice";
    case "sources":
      return "Sources";
    case "source":
      return "Source";
    case "account":
      return "Account";
  }
}

/**
 * The subset of a server `Alert` a client needs in order to route it. Kept
 * structurally minimal so the routing rule does not have to be revised every
 * time the alert record grows a field.
 */
export interface RoutableAlert {
  readonly alertId: string;
  readonly subjectType: string;
  readonly subjectId: string;
  readonly sourceRequestId?: string | null;
}

/** `subjectType` values this client knows how to open, and where they open. */
const SUBJECT_DESTINATIONS: Readonly<Record<string, Destination["kind"]>> = NAVIGATION_SUBJECT_DESTINATIONS;

/**
 * Subject kinds whose object has no page of its own yet.
 *
 * A stalled device and a drifting mirror are both real objects the server names
 * as an alert's subject, and neither has a destination — so without this they
 * fell through to the generic fallback, which is the "everything lands in one
 * inbox" outcome the routing rule exists to prevent. Health is the closest
 * surface that actually explains them. When a device page exists, move `device`
 * into `SUBJECT_DESTINATIONS` and this entry goes away.
 */
const SUBJECT_INDEX_DESTINATIONS: Readonly<Record<string, Destination>> = Object.fromEntries(
  Object.entries(NAVIGATION_SUBJECT_INDEXES).map(([subject, kind]) => [subject, { kind }]),
) as Readonly<Record<string, Destination>>;

/**
 * **An alert opens the object that explains it.**
 *
 * A failed run opens its run page, a completed request opens its request, a
 * reply opens its conversation. Only when the subject is unusable — an unknown
 * `subjectType` from a newer server, or an id that does not parse — does this
 * fall back: first to the request that caused the alert, so the user still
 * lands near the work, and only last to the alert's own page.
 */
export function alertDestination(alert: RoutableAlert): Destination {
  const kind = SUBJECT_DESTINATIONS[alert.subjectType];
  if (kind && isValidId(alert.subjectId)) return { kind, id: alert.subjectId } as Destination;
  const index = SUBJECT_INDEX_DESTINATIONS[alert.subjectType];
  if (index) return index;
  const sourceRequestId = alert.sourceRequestId ?? "";
  if (isValidId(sourceRequestId)) return { kind: "request", id: sourceRequestId };
  return isValidId(alert.alertId) ? { kind: "alert", id: alert.alertId } : { kind: "alerts" };
}

/** True when two destinations address the same thing. */
export function sameDestination(left: Destination, right: Destination): boolean {
  return formatDestination(left) === formatDestination(right);
}

/**
 * Destinations the shell draws with the note list rather than inside the
 * workspace: the list itself, and the two that are modal dialogs over it.
 */
const OUTSIDE_WORKSPACE: readonly Destination["kind"][] = ["vault", "search", "account"];

/**
 * Whether this destination names a domain object — an alert, a question, a
 * source — rather than a note or the vault itself.
 *
 * The domain section and the reader share `.workspace`, so exactly one of them
 * is on screen at a time and both derive from this one answer.
 */
export function showsDomainView(destination: Destination): boolean {
  return !OUTSIDE_WORKSPACE.includes(destination.kind) && destination.kind !== "note";
}

/**
 * Whether this destination renders into the workspace at all, by naming either
 * a note or a domain object.
 *
 * At phone width the workspace is off screen until something is put in it, so
 * this is the question the mobile layout asks. See `mobile-workspace.ts`.
 */
export function fillsWorkspace(destination: Destination): boolean {
  return !OUTSIDE_WORKSPACE.includes(destination.kind);
}
